import React from "react";
import { Check, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ScrollReveal from "../components/site/ScrollReveal";
import {
  FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { blogsForService } from "../data/internalLinks";

export default function Packages() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <Philosophy />
      <PackageTiers />
      <WhatIsIncluded />
      <Process />
      <PriceHook />
      <SisterServices keys={["menu", "food", "venue", "brand"]} />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More on pricing"
        italic="and packaging shoots."
        theme="ink"
        cols={3}
        items={blogsForService("packages")}
      />
      <FAQSection
        eyebrow="Common questions · Packages"
        headline="Process, scope,"
        italic="and pricing."
        intro="If your question isn't covered below, the fastest path is to call directly — most quotes are confirmed in under five minutes."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Ready to scope"
        italic="your shoot?"
        intro="Quick form below or call directly — quotes confirmed inside one business day. No marketing chase, no spam, no AI follow-ups."
        backdropSlug="hero-banquet-01"
        formTitle="Tell us about your shoot."
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="hero-banquet-01" alt="Restaurant photography packages Melbourne" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/40 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Photography Packages" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Packages · How a Melbourne hospitality shoot runs
        </p>

        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}>
          Three ways
          <br />
          to <span className="italic font-light text-ember">work with us.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          Every venue's brief is different — but the way we run a shoot day
          isn't. Three core package shapes, transparent process, hand-confirmed
          quotes. Pricing scopes by menu size and the channels you're shooting
          for, not by per-hour billing.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <a href="#tiers" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">See The Packages</CTAButton>
          </a>
          <a href="#process" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">How A Shoot Runs</CTAButton>
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-12 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl border-t border-bone/15 pt-6">
        {[
          ["Quote", "Same day"],
          ["Shoot", "1–3 day kit"],
          ["Delivery", "5–7 days"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider2 text-ember mb-1.5 font-bold">{k}</p>
            <p className="font-sans font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Philosophy = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            How we price · Honest hospitality
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            We quote the shoot,
            <br />
            <span className="italic font-light text-ember">not the hour.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            Pricing scopes by menu size, channel and complexity — not by
            stopwatch. You always get a written quote before the shoot. No
            hidden retouching fees, no per-channel licence top-ups, no surprise
            invoices.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const TIERS = [
  {
    id: "refresh",
    label: "01 · The Refresh",
    title: "Quick menu refresh",
    blurb: "A short on-location session covering 15–25 of your most-ordered dishes plus a couple of venue moments. The most-booked package — most cafés and takeaway shops fit here.",
    bestFor: "Cafés · takeaway · small menus",
    fromLabel: "From $540 ·",
    fromDetail: "15 images · half-day",
    bullets: [
      "15–25 dishes on location",
      "Square + landscape crops per dish",
      "2–3 venue / interior frames",
      "Files named to your menu",
      "5–7 day delivery",
    ],
    cta: { label: "Plan a refresh", href: "/menu-photography" },
  },
  {
    id: "menu",
    label: "02 · The Full Menu",
    title: "Full-menu library",
    blurb: "The complete library shoot — every dish you sell, on every channel. One on-location day delivers Uber Eats, DoorDash, Menulog, Google, website and Instagram-ready assets.",
    bestFor: "Restaurants · multi-channel · delivery-led",
    fromLabel: "From $1,000 ·",
    fromDetail: "Volume tier · $10 per image at scale",
    bullets: [
      "Full menu — 30 to 100+ dishes",
      "Hero plates lit for campaign use",
      "Venue + service moments included",
      "Delivery-app ready crops",
      "Google Business library bonus",
      "5–7 day delivery",
    ],
    cta: { label: "Plan the full menu", href: "/menu-photography" },
    featured: true,
  },
  {
    id: "brand",
    label: "03 · The Brand Day",
    title: "Editorial brand day",
    blurb: "A full editorial production day for new openings, expansions, refreshes or franchise rollouts. Coordinated with your designer, delivered as a structured asset library.",
    bestFor: "New openings · refreshes · campaigns",
    fromLabel: "From $1,800 ·",
    fromDetail: "Bespoke scope · multi-day on request",
    bullets: [
      "Multi-frame brand library",
      "Hero plates · lifestyle · venue",
      "Press kit & PR-ready assets",
      "Designer / agency coordination",
      "Full owned & earned licence",
      "7–10 day delivery",
    ],
    cta: { label: "Scope a brand day", href: "/brand-photography" },
  },
];

const PackageTiers = () => (
  <section id="tiers" className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Three package shapes · Pick the closest fit
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Refresh.
            <br />
            <span className="italic font-light text-ember">Menu. Brand.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            Most venues fit one of these three shapes. We confirm the final
            price on the call — based on your exact menu size, suburb and
            channel mix.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-bone/10 border border-bone/10">
        {TIERS.map((t) => (
          <article
            key={t.id}
            data-testid={`package-${t.id}`}
            className={`relative p-7 md:p-10 flex flex-col ${t.featured ? "bg-[#161613]" : "bg-ink"}`}
          >
            {t.featured && (
              <span className="absolute -top-px right-0 bg-ember text-bone text-[10px] uppercase tracking-wider2 font-bold px-3 py-1.5">
                Most booked
              </span>
            )}
            <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-4">{t.label}</p>
            <h3 className="font-display text-3xl md:text-4xl tracking-editorial leading-tight">{t.title}</h3>
            <p className="mt-4 text-sm text-bone/65 italic">{t.bestFor}</p>
            <p className="mt-6 text-base md:text-lg text-bone/85 leading-relaxed">{t.blurb}</p>

            <div className="mt-8 pt-6 border-t border-bone/15">
              <p className="font-sans font-bold text-lg tracking-tight">
                {t.fromLabel} <span className="text-bone/65 font-normal text-sm">{t.fromDetail}</span>
              </p>
            </div>

            <ul className="mt-7 space-y-3 flex-1">
              {t.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-bone/80">
                  <Check size={14} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <Link to={t.cta.href} className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-wider2 font-bold text-ember hover:text-bone transition-colors group">
              {t.cta.label}
              <ArrowUpRight size={14} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </article>
        ))}
      </div>

      <p className="mt-10 text-xs uppercase tracking-wider2 text-bone/45 max-w-3xl leading-relaxed font-medium">
        All prices indicative ex-GST. Final quote depends on menu size, suburb,
        access window and channel mix. Most quotes confirmed in under five
        minutes by phone — no marketing chase.
      </p>
    </div>
  </section>
);

const INCLUDED = [
  { title: "Pre-shoot consult", desc: "A 15-minute call to lock the brief — menu list, channels, deadlines, suburb." },
  { title: "On-location production", desc: "We come to you. Full lighting, surface and props kit. No studio drop-offs." },
  { title: "Real plating", desc: "Your kitchen plates the food. We light it and frame it. No plastic, no styling tricks." },
  { title: "Multi-channel crops", desc: "Square + landscape per dish, sized for Uber Eats, Google, web and Instagram." },
  { title: "Edited delivery", desc: "Master files plus channel-ready crops in a single shareable folder, named to your menu." },
  { title: "Full usage licence", desc: "Paid social, OOH, print, EDM, packaging, PR — every owned and earned channel." },
  { title: "One free re-edit", desc: "Per dish, no questions. If a particular frame doesn't sit right, we re-edit it." },
  { title: "Hand-confirmed quote", desc: "Real conversations, no auto-replies. Quotes confirmed inside one business day." },
];

const WhatIsIncluded = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            What every package includes
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            No extras.
            <br />
            <span className="italic font-light text-ember">No surprises.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            Every shoot — Refresh, Menu or Brand — includes the same eight
            things below as standard. No per-channel licence fees, no
            retouching surprises, no agency markups.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
        {INCLUDED.map((i, idx) => (
          <ScrollReveal key={i.title} delay={idx * 40}>
            <div className="bg-bone p-6 md:p-7 h-full">
              <Check size={20} strokeWidth={2} className="text-ember mb-4" />
              <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight">{i.title}</h3>
              <p className="mt-3 text-sm text-char leading-relaxed">{i.desc}</p>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </div>
  </section>
);

const STEPS = [
  { n: "01", title: "Quick call", desc: "A 5–15 minute call to scope menu, channels, suburb and deadline. Most quotes are confirmed on this call." },
  { n: "02", title: "Written quote & date", desc: "We send a written quote the same day with a locked shoot date. You confirm by reply." },
  { n: "03", title: "Shoot day", desc: "We arrive with full lighting and prop kit. Your kitchen plates the food. Usually 4–8 hours on-location." },
  { n: "04", title: "Edit & deliver", desc: "Edited master files + channel-ready crops delivered in 5–7 business days. One folder, named to your menu." },
];

const Process = () => (
  <section id="process" className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            How a shoot runs · From call to delivery
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Four steps,
            <br />
            <span className="italic font-light text-ember">zero guesswork.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed">
            We handle the production logistics. You handle the plating. Most
            venues book and shoot within 7–14 days of the first call.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {STEPS.map((s) => (
          <div key={s.n} className="bg-ink p-7 md:p-8">
            <span className="font-display italic text-2xl text-ember leading-none">{s.n}</span>
            <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-3">{s.title}</h3>
            <p className="mt-4 text-sm text-bone/75 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PriceHook = () => (
  <section className="relative bg-bone text-ink py-24 lg:py-36 overflow-hidden">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 items-center">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            On the $10-per-image claim · Honest fine print
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}>
            $10 per image is real —
            <br />
            <span className="italic font-light text-ember">but it isn't every shoot.</span>
          </h2>

          <div className="mt-10 space-y-5 text-base md:text-lg text-char leading-relaxed max-w-2xl">
            <p>
              Our volume tier kicks in at <strong className="text-ink">100+ images</strong> from
              a single venue, minimum spend $1,000. That's where the $10
              per-image rate applies — typically full restaurant menu shoots
              with delivery, dine-in and Google all in one library.
            </p>
            <p>
              For smaller menus (15–25 images) the rate sits at <strong className="text-ink">$30 per image</strong>,
              and the 21–99 range sits at <strong className="text-ink">$25 per image</strong>.
              The reason: production time per image goes down as menu size goes
              up. We pass that saving on directly.
            </p>
            <p className="text-sm text-char-mute italic pt-2">
              No fine print at the door. The number you're quoted is the number
              you're invoiced.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a href="/#planner">
              <CTAButton variant="secondary" size="md">Estimate My Shoot</CTAButton>
            </a>
            <a href="tel:0494756111">
              <CTAButton variant="link" size="md">Or call 0494 756 111</CTAButton>
            </a>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5">
          <div className="bg-ink text-bone p-10 lg:p-12 relative">
            <p className="text-xs uppercase tracking-wider2 text-ember mb-6 font-bold">Volume tier</p>
            <div className="space-y-6">
              <div className="flex items-baseline justify-between gap-4 pb-4 border-b border-bone/15">
                <span className="text-sm md:text-base text-bone/70">15–20 images</span>
                <span className="font-sans font-bold text-2xl tracking-tight">$30<span className="text-sm text-bone/55 font-normal"> / image</span></span>
              </div>
              <div className="flex items-baseline justify-between gap-4 pb-4 border-b border-bone/15">
                <span className="text-sm md:text-base text-bone/70">21–99 images</span>
                <span className="font-sans font-bold text-2xl tracking-tight">$25<span className="text-sm text-bone/55 font-normal"> / image</span></span>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <div>
                  <span className="text-sm md:text-base text-bone/70">100+ images</span>
                  <p className="text-[10px] uppercase tracking-wider2 text-ember mt-1 font-bold">Min spend $1,000</p>
                </div>
                <span className="font-sans font-bold text-3xl tracking-tight text-ember">$10<span className="text-sm text-bone/55 font-normal"> / image</span></span>
              </div>
            </div>
            <p className="mt-8 text-xs text-bone/55 leading-relaxed">
              All prices indicative ex-GST. Final quote depends on menu, suburb
              and channel mix.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FAQS = [
  { q: "Why three packages instead of a flat menu?", a: "Every venue has a different size, channel mix and deadline — but the three package shapes (Refresh, Full Menu, Brand) cover 95% of briefs. The fourth path is a fully bespoke quote, which we'll always do on a call if your brief doesn't fit." },
  { q: "Can I bolt reels or venue onto a package?", a: "Yes — reels add-ons from $350 to any photo day, venue shoots from $450 added to a menu day. Most clients book at least two services together because the on-location lighting carries across, and we can pass the saving on." },
  { q: "Do I have to pay upfront?", a: "No. A 25% deposit secures the shoot date. Balance is due on delivery — usually 5–7 business days after the shoot. For first-time clients we send a written quote and tax invoice before any payment is requested." },
  { q: "What if I'm not happy with the photos?", a: "Every shoot includes one free re-edit per dish, no questions. If a particular frame doesn't sit right, we re-edit. If a full dish needs reshooting (rare, but it happens for plating reasons), we'll come back inside 30 days at no extra cost." },
  { q: "Can you shoot regional Victoria or interstate?", a: "Yes for franchise rollouts and brand campaigns — travel costs estimated upfront. For one-off shoots, our coverage is all of Greater Melbourne with no travel fees inside the metro area." },
  { q: "How fast can a shoot happen from first call?", a: "Most shoots happen within 7–14 days of the first call. Urgent (3–5 day) bookings are usually possible — depending on existing schedule and how complex the brief is. Just mention the deadline on the call." },
];
